import * as React from 'react';
import { connect } from 'react-redux';
import { customerIsLegacyCommission } from '../../../data/entities/commission/selectors';
import { isAdmin } from '../../../entities/admin/selectors';

import { memoizedGroupBySelector } from '@travelnest/entities';
import { ICustomer } from '../../../data/entities/customer';
import { INotification } from '../../../data/entities/notification';
import { active, deduped } from '../../../data/entities/notification/helpers';
import { IProperty } from '../../../data/entities/property';
import { IPropertyScoreSummary } from '../../../data/entities/property-score';
import { actions } from '../../../data/store';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { actionsFromNotifications, sortByPriority } from '../../components/action/helper';
import { IAction } from '../../components/action/types';
import { useDataLoad } from '../../helpers/hooks';
import { getCustomerActions } from '../actions/actions';
import { HomeComponent } from './Home';

interface IHomeContainerProps {
	creatable: boolean;
	customerId: string;
	actions: IAction[];
	notifications: Array<{ count: number, notification: INotification }>;
	getActions: (ownerId: string) => any;
	getNotifications: (ownerId: string) => any;
	dismissNotification: (id: number) => any;
	getCustomerPropertyScores: (ownerId: string) => void;
	getCustomerProperties: (ownerId: string) => void;
	getCustomerData: (ownerId: string) => void;
	isLegacyCommission: boolean;
	isAdmin: boolean;
	customerPropertyScores: IPropertyScoreSummary[];
	customerProperties: IProperty[];
	customerData: ICustomer;
}

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	getActions: (ownerId: string) => dispatch(getCustomerActions(ownerId)).catch((e: any) => e),
	getNotifications: (ownerId: string) => dispatch(actions.notifications.getByOwnerId(ownerId, ['web'])),
	dismissNotification: (id: number) => dispatch(actions.notifications.dismiss(id)),
	getCustomerPropertyScores: (ownerId: string) => dispatch(actions.propertyScores.getByOwnerId(ownerId)),
	getCustomerProperties: (ownerId: string) => dispatch(actions.property.getByOwnerId(ownerId)),
	getCustomerData: (ownerId: string) => dispatch(actions.customer.getById(ownerId)).catch((e: any) => e),
});

const mapStateToProps = (state: IState) => {
	const customerId = currentUserId(state); // imported from "entities/auth/session/selectors"
	const customerActions = state.views.actions.byOwnerId[customerId];
	const notifications = deduped(active(state.data.notifications.byId, customerId));
	const customerPropertyScores = state.data.propertyScores.byOwnerId[customerId] || [];
	const customerProperties = memoizedGroupBySelector('owner_id', state.data.properties.byId)[customerId] || [];
	const customerData = state.data.customers.byId[customerId];
	return {
		actions: customerActions,
		creatable: state.entities.flags.userFeatures.canCreateProperty,
		isLegacyCommission: customerIsLegacyCommission(state, customerId),
		customerId,
		notifications,
		isAdmin: isAdmin(state),
		customerPropertyScores: customerPropertyScores.sort((a, b) => a.total_score - b.total_score),
		customerProperties,
		customerData
	};
};

const HomeContainer = (props: IHomeContainerProps) => {
	const {
		actions: acts,
		notifications: notifs,
		customerId,
		getNotifications,
		getActions,
		customerPropertyScores,
		getCustomerPropertyScores,
		customerProperties,
		getCustomerProperties,
		customerData,
		getCustomerData
	} = props;

	const notificationsLoading = useDataLoad(!(acts && acts.length), () => getNotifications(customerId));
	const actionsLoading = useDataLoad(!(notifs && notifs.length), () => getActions(customerId));
	const customerPropertyScoresLoading = useDataLoad(
		!(customerPropertyScores && customerPropertyScores.length),
		async () => getCustomerPropertyScores(customerId)
	);
	const customerPropertiesLoading = useDataLoad(
		customerProperties.length === 0,
		async () => getCustomerProperties(customerId)
	);
	const customerDataLoading = useDataLoad(
		!customerData,
		async () => getCustomerData(customerId)
	);

	let allActions: IAction[] = [];
	if (acts) {
		allActions = allActions.concat(acts);
	}
	if (notifs) {
		allActions = allActions.concat(
			actionsFromNotifications(notifs.map(({ notification: n }) => n), props.dismissNotification)
		);
	}
	allActions = sortByPriority(allActions);

	return <HomeComponent
		{...props}
		loadingActions={actionsLoading || notificationsLoading || customerPropertyScoresLoading || customerPropertiesLoading || customerDataLoading}
		actions={allActions}
		propertyScores={customerPropertyScores}
		customerProperties={customerProperties}
		customerData={customerData}
	/>;
};

export const Home = connect(mapStateToProps, mapDispatchToProps)(HomeContainer);
